import * as Dashboards from "@highcharts/dashboards";
import Highcharts from "highcharts";

import "@highcharts/dashboards/css/dashboards.css";

import { useEffect, useRef } from "react";
import { Box, CircularProgress } from "@mui/material";
import GlobalStyles from "@mui/system/GlobalStyles";
import LayoutModule from "@highcharts/dashboards/modules/layout";

Dashboards.HighchartsPlugin.custom.connectHighcharts(Highcharts);
Dashboards.PluginHandler.addPlugin(Dashboards.HighchartsPlugin);

LayoutModule(Dashboards);

const injectGlobalStyles = (
  <GlobalStyles
    styles={{
      ".highcharts-dashboards-component-title": {
        textAlign: "start",
        fontSize: "1rem",
        fontWeight: 100,
        paddingLeft: 2,
      },
      ".highcharts-dashboards-component-kpi-value": {
        fontWeight: "500",
        fontSize: "5rem",
        minHeight: 80,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      },
      ".highcharts-background, .highcharts-dashboards-wrapper": {
        backgroundColor: "#dff2ee !important",
        padding: "0 !important",
        minHeight: "0 !important",
      },
      "#kpi-cell-1, #kpi-cell-2": {
        height: 230,
      },
      ".highcharts-dashboards-cell > .highcharts-dashboards-component": {
        backgroundColor: "#08404d !important",
        margin: "0 !important",
      },
      ".highcharts-color-0": {
        stroke: "#27CCC7 !important",
      },
      ".highcharts-dashboards-component .highcharts-dashboards-component-kpi-chart-container": {
        fill: "#08404d !important",
      },
      ".highcharts-dashboards-row": {
        gap: 5,
      },
    }}
  />
);

const HighchartsDashboard = ({ kpiComponents, guiLayouts, isLoading }) => {
  const dashboardContainerRef = useRef(null);
  const boardRef = useRef(null);

  useEffect(() => {
    if (!dashboardContainerRef.current) {
      return;
    }

    Highcharts.setOptions({
      chart: {
        styledMode: true,
      },
    });

    const dashboardComponentsConfig = kpiComponents.map((kpi) => {
      return {
        type: "KPI",
        renderTo: kpi.id,
        title: kpi.title,
        value: kpi.value,
        valueFormat: kpi.valueFormat,
        subtitle: kpi.subtitle,
        linkedValueTo: kpi.linkedValueTo || { enabled: false },
        chartOptions: {
          chart: { styledMode: true },
          series: [
            {
              type: kpi.seriesType || "spline",
              enableMouseTracking: false,
              dataLabels: { enabled: false },
              data: kpi.seriesData || [],
            },
          ],
          ...kpi.kpiChartOptionsOverrides,
        },
      };
    });

    const dashboardConfig = {
      gui: {
        layouts: guiLayouts,
      },
      components: dashboardComponentsConfig,
    };

    if (isLoading) {
      boardRef.current = Dashboards.board(dashboardContainerRef.current, dashboardConfig, true);
    } else {
      boardRef.current = Dashboards.board(dashboardContainerRef.current, dashboardConfig, false);
    }

    return () => {
      if (boardRef.current && typeof boardRef.current.destroy === "function") {
        boardRef.current.destroy();
        boardRef.current = null;
      } else {
        console.warn(
          "Attempted to destroy Highcharts Dashboards, but 'board' was not a valid instance or destroy function was missing."
        );
      }
    };
  }, [kpiComponents, guiLayouts, isLoading]);

  if (isLoading) {
    return (
      <div>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "5px",
            padding: 0,
            backgroundColor: "#dff2ee",
          }}
        >
          {kpiComponents.map((kpi) => (
            <Box
              key={kpi.id}
              sx={{
                backgroundColor: "#08404d",
                height: 230,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress sx={{ color: "#27CCC7" }} />
            </Box>
          ))}
        </Box>
        {injectGlobalStyles}
      </div>
    );
  }

  return (
    <div>
      <div
        ref={dashboardContainerRef}
        style={{
          width: "100%",
          height: "100%",
        }}
      />
      {injectGlobalStyles}
    </div>
  );
};

export default HighchartsDashboard;
